import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Rocket } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { PageLoadingState } from '@/components/glitch-ui/empty-state';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';

/**
 * Address a specific organization's business page by path. External surfaces
 * (the admin dashboard's Clients rows) link here so the visitor lands on the
 * right client without touching the account switcher: the route switches the
 * active organization exactly as the organizations page does, then redirects
 * to /business, which shows the org's business and its generation progress.
 * An organization the visitor holds no membership of is not in their list, so
 * the redirect falls back to /organizations rather than silently showing
 * another client's business.
 */
function BusinessByOrganization() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const { organizations, organizationData, switchOrganization, isSwitching } = useOrganizationInitialization();
  const handled = useRef(false);

  useEffect(() => {
    // The app shell restores the organization from the server profile; until it has, there is nothing to compare.
    if (!organizationData || isSwitching || handled.current) return;
    handled.current = true;

    const target = organizations.find((org) => org.id === Number(orgId));
    if (!target) {
      navigate({ to: '/organizations', replace: true });
      return;
    }
    if (organizationData?.id === target.id) {
      navigate({ to: '/business', replace: true });
      return;
    }
    // finally, not then: the switch sets the organisation before anything
    // that could reject, so /business is the right destination either way —
    // and the visitor is never left on this spinner.
    switchOrganization(target).finally(() => navigate({ to: '/business', replace: true }));
  }, [isSwitching, organizations, organizationData, orgId, navigate, switchOrganization]);

  return <PageLoadingState />;
}

export const Route = createFileRoute('/_authenticated/_layout/business/$orgId')({
  component: BusinessByOrganization,
  staticData: {
    title: 'Business',
    icon: Rocket,
  },
});
